import React, { useState } from "react";
import theme from "../../data/theme";
import { shareUrl } from "../../utils/liveBoardPublish";

/**
 * The watch link, and the sync state — SPLIT, because they are two different
 * kinds of thing that were sharing one row.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE LINK IS A SETUP CONTROL. You copy it once, before anyone sits down.
 * It lives in the board menu (BoardMenu.jsx) and costs no board on any screen.
 *
 * 🔴 THE FAILED SYNC IS AN ALARM, AND IT STAYS ON SCREEN AT EVERY SCALE.
 * A publish failure means eleven people are holding a link that has stopped
 * updating, and they have no way to find that out from their side — the
 * spectator page's own staleness marker says "not updating", which reads as a
 * network problem on their end. The only person who can act on it is the
 * operator, and he will not open a menu to check something he has no reason to
 * suspect. So it renders itself, unprompted, wherever the board is.
 *
 * ⚠️ THIS RECLASSIFIES WHAT THIS FILE USED TO SAY. The old header read "THE
 * INDICATOR IS INFORMATION, NOT AN ALARM" — that was about not INTERRUPTING:
 * no dialog, no retry button, nothing that can stop or slow the person entering
 * picks. All of that still holds and is why SyncAlarm is a line and not a modal.
 * What changed is where it may be HIDDEN, which the old row never had to answer
 * because it was always on screen below TV and simply absent above it.
 *
 * 🔴 THE TV CASE IS WHY THIS SPLIT EXISTS AT ALL. `{!tv && <ShareBar/>}` dropped
 * both halves at the largest preset, on the reasoning that nobody copies a URL
 * off a television. True — and it also took the alarm away, which was fine when
 * TV meant a separate screen and is not fine now that Ben mirrors: the TV IS his
 * operating screen, so the one surface where a dead feed most matters was the
 * one surface that could not report it.
 *
 * There is still no retry button: the next pick republishes the whole board, so
 * a dropped POST self-heals within one entry and a manual retry would only
 * invite a storm.
 * ══════════════════════════════════════════════════════════════════════
 */

/** The three-state sync line, shared by both halves so they cannot drift. */
function syncText(syncOk) {
  if (syncOk === false) return "not syncing, your board is still saved here";
  return syncOk ? "synced" : "not published yet";
}

/**
 * ShareLink — the copy affordance. Rendered inside the board menu, which is why
 * it carries no margin and no "gone at TV" condition: a menu costs nothing
 * until it is opened, so there is nothing to reclaim by hiding it.
 */
export default function ShareLink({ shareId, syncOk }) {
  const [copied, setCopied] = useState(false);
  if (!shareId) return null;
  const url = shareUrl(shareId);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure origin, permissions) — the input below is
      // selectable, so there is always a way to get the link.
    }
  };

  const dot = syncOk === false ? "#eab308" : syncOk ? "#22c55e" : theme.colors.text.faint;

  return (
    <div data-testid="share-link" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <input
        readOnly value={url} onFocus={e => e.target.select()}
        aria-label="Read-only link to share with your league"
        style={{
          width: "100%", minWidth: 0, background: theme.colors.bg.raised,
          border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.radius.sm,
          color: theme.colors.text.secondary, fontFamily: "inherit",
          fontSize: theme.fontSize.caption, padding: "5px 9px", outline: "none",
        }}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={copy} style={{
          background: copied ? theme.colors.status.success : theme.colors.bg.raised,
          border: `1px solid ${copied ? theme.colors.status.success : theme.colors.border.default}`,
          borderRadius: theme.radius.sm, color: copied ? "#04240f" : theme.colors.text.primary,
          fontFamily: "inherit", fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.bold,
          padding: "5px 12px", cursor: "pointer", whiteSpace: "nowrap",
        }}>{copied ? "Copied" : "Copy"}</button>

        <a href={url} target="_blank" rel="noreferrer" style={{
          fontSize: theme.fontSize.caption, color: theme.colors.text.muted, whiteSpace: "nowrap",
        }}>open</a>

        <span style={{
          marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
          fontSize: theme.fontSize.micro, color: syncOk === false ? "#fde68a" : theme.colors.text.faint,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot }} />
          {syncText(syncOk)}
        </span>
      </div>
    </div>
  );
}

/**
 * SyncAlarm — on screen whenever the feed is down, and invisible otherwise.
 *
 * 🔴 IT RENDERS NOTHING IN THE HEALTHY CASES, which is what lets it live above
 * the board at every scale including TV: `syncOk === true` and the pre-publish
 * `null` both cost zero pixels, so the reassuring states stay in the menu next
 * to the link — where you are already looking at whether the link works — and
 * only the state somebody has to act on takes board.
 */
export function SyncAlarm({ syncOk }) {
  if (syncOk !== false) return null;
  return (
    <div data-testid="sync-alarm" style={{
      display: "flex", alignItems: "center", gap: 8,
      background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.40)",
      borderRadius: theme.radius.md, padding: "6px 12px", marginBottom: 8,
      fontSize: theme.fontSize.caption, color: "#fde68a", fontWeight: theme.fontWeight.bold,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#eab308", flexShrink: 0 }} />
      <span>The watch link is {syncText(false)}. The room's page has stopped updating.</span>
    </div>
  );
}
